import {
  ReviewerStatus,
  ReviewMode,
  SubmissionStatus,
  UserType,
} from "encamp-prisma/generated/browser";

/**
 * The reviewer fields needed to decide whose turn it is. Kept minimal so both
 * the API resolver guard and the stepper UI can pass their own shapes.
 */
export type ReviewerForTurn = {
  userId: string;
  /**
   * The reviewer's `User.type`. Turn phasing keys off the user's actual type —
   * NOT the `SubmissionReviewer.isStaff` column — so it always agrees with the
   * phase routing in `startReviewWorkflow`/`completeStaffReview` (which use
   * `isStaffUser`). A staff user added through the regular reviewer picker has
   * `isStaff=false` on their row but still reviews in the staff phase.
   */
  userType: string;
  isSigner: boolean;
  reviewOrder: number;
  reviewerStatus?: string | null;
};

/**
 * Mirrors `STAFF_USER_TYPES` from `encamp-shared/auth`, which cannot be
 * imported here because the auth module pulls in the server-only prisma
 * client and this module must stay browser-safe.
 */
const STAFF_USER_TYPES: ReadonlyArray<string> = [UserType.STAFF_ADMIN];

function isStaffReviewer(reviewer: ReviewerForTurn): boolean {
  return STAFF_USER_TYPES.includes(reviewer.userType);
}

function hasApproved(reviewer: ReviewerForTurn): boolean {
  return reviewer.reviewerStatus === ReviewerStatus.APPROVED;
}

/**
 * Whether `userId` is a reviewer whose turn it is to act right now.
 *
 * Phase: staff reviewers act during `ENCAMP_INITIAL_REVIEW`, non-staff during
 * `IN_REVIEW`; signers are excluded (they sign, not review). Parallel → any
 * not-yet-approved reviewer in the active phase may act; sequential → only the
 * lowest-`reviewOrder` not-yet-approved reviewer. Co-located with the form
 * registry so the resolver guard and the UI gate agree.
 */
export function isActiveReviewer(
  userId: string,
  {
    status,
    reviewMode,
    reviewers,
  }: { status: string; reviewMode: string; reviewers: ReviewerForTurn[] }
): boolean {
  const isStaffPhase = status === SubmissionStatus.ENCAMP_INITIAL_REVIEW;
  const isCustomerPhase = status === SubmissionStatus.IN_REVIEW;
  if (!isStaffPhase && !isCustomerPhase) {
    return false;
  }

  const phaseReviewers = reviewers.filter(
    (reviewer) => !reviewer.isSigner && isStaffReviewer(reviewer) === isStaffPhase
  );

  const mine = phaseReviewers.find((reviewer) => reviewer.userId === userId);
  if (!mine || hasApproved(mine)) {
    return false;
  }

  if (reviewMode === ReviewMode.SEQUENTIAL) {
    const earlierPending = phaseReviewers.some(
      (reviewer) => reviewer.reviewOrder < mine.reviewOrder && !hasApproved(reviewer)
    );
    if (earlierPending) {
      return false;
    }
  }

  return true;
}
